import { useState } from 'react';
import { backend } from '../api/backend';
import { useProject } from '../state/project';
import './PublishButton.css';

/**
 * Editor-header control: publish the project to a stable public URL, show the
 * shareable link with copy + unpublish. Reflects the loaded project's
 * is_public/public_token (from the detail payload).
 */
export default function PublishButton() {
  const project = useProject((s) => s.project);
  const setPublish = useProject((s) => s.setPublish);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!project) return null;

  const shareUrl = project.public_token
    ? `${window.location.origin}/p/${project.public_token}`
    : '';

  async function publish() {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const r = await backend.projects.publish(project.id);
      setPublish(r.is_public, r.public_token);
      setOpen(true);
    } catch {
      setError('Could not publish — is the backend running?');
    } finally {
      setBusy(false);
    }
  }

  async function unpublish() {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const r = await backend.projects.unpublish(project.id);
      setPublish(r.is_public, r.public_token);
    } catch {
      setError('Could not unpublish.');
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — user can select manually */
    }
  }

  return (
    <div className="publish">
      <button
        className={project.is_public ? 'active' : ''}
        disabled={busy}
        onClick={() => (project.is_public ? setOpen((v) => !v) : void publish())}
      >
        {project.is_public ? '● Published' : 'Publish'}
      </button>

      {open && project.is_public && (
        <div className="publish-pop" onClick={(e) => e.stopPropagation()}>
          <p className="publish-label">Anyone with this link can watch:</p>
          <div className="publish-row">
            <input className="publish-url" readOnly value={shareUrl} onFocus={(e) => e.target.select()} />
            <button className="ghost" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="publish-actions">
            <a href={shareUrl} target="_blank" rel="noopener">
              Open viewer ↗
            </a>
            <button className="danger ghost" disabled={busy} onClick={() => void unpublish()}>
              Unpublish
            </button>
          </div>
          {error && <p className="publish-error">{error}</p>}
        </div>
      )}
      {error && !open && <span className="publish-error">{error}</span>}
    </div>
  );
}
